import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import { Loader2, Trash2, User, UsersRound } from 'lucide-react'
import type { League, LeagueFormat, MatchFormat } from './types'

interface LeagueSettingsDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  league: League
  // Form state
  name: string
  onNameChange: (value: string) => void
  description: string
  onDescriptionChange: (value: string) => void
  format: LeagueFormat
  onFormatChange: (value: LeagueFormat) => void
  matchFormat: MatchFormat
  onMatchFormatChange: (value: MatchFormat) => void
  autoApprove: boolean
  onAutoApproveChange: (value: boolean) => void
  status: 'active' | 'archived'
  onStatusChange: (value: 'active' | 'archived') => void
  // Actions
  onSave: () => Promise<void>
  onDelete: () => void
  saving: boolean
  deleting: boolean
  error: string | null
}

export function LeagueSettingsDialog({
  open,
  onOpenChange,
  league,
  name,
  onNameChange,
  description,
  onDescriptionChange,
  format,
  onFormatChange,
  matchFormat,
  onMatchFormatChange,
  autoApprove,
  onAutoApproveChange,
  status,
  onStatusChange,
  onSave,
  onDelete,
  saving,
  deleting,
  error,
}: LeagueSettingsDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>League Settings</DialogTitle>
          <DialogDescription>Update the settings for {league.name}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 pt-4">
          <div className="space-y-2">
            <Label>Name</Label>
            <Input value={name} onChange={e => onNameChange(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea value={description} onChange={e => onDescriptionChange(e.target.value)} rows={3} />
          </div>
          <div className="space-y-2">
            <Label>Format</Label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => onFormatChange('singles')}
                className={`flex items-center gap-2 justify-center rounded-md border-2 p-3 transition-colors ${
                  format === 'singles'
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:border-primary/50'
                }`}
              >
                <User className="h-5 w-5" />
                <span className="font-medium">Singles</span>
              </button>
              <button
                type="button"
                onClick={() => onFormatChange('doubles')}
                disabled
                className={`flex flex-col items-center justify-center rounded-md border-2 p-3 transition-colors relative ${
                  format === 'doubles'
                    ? 'border-primary bg-primary/5'
                    : 'border-border opacity-50 cursor-not-allowed'
                }`}
              >
                <div className="flex items-center gap-2">
                  <UsersRound className="h-5 w-5" />
                  <span className="font-medium">Doubles</span>
                </div>
                <span className="text-[10px] text-muted-foreground mt-1">Coming soon</span>
              </button>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Match Format</Label>
            <div className="grid grid-cols-3 gap-2">
              <button
                type="button"
                onClick={() => onMatchFormatChange('bestOf1')}
                className={`flex flex-col items-center justify-center rounded-md border-2 p-3 transition-colors ${
                  matchFormat === 'bestOf1'
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:border-primary/50'
                }`}
              >
                <span className="text-lg font-bold">1</span>
                <span className="text-xs text-muted-foreground">Single Game</span>
              </button>
              <button
                type="button"
                onClick={() => onMatchFormatChange('bestOf3')}
                className={`flex flex-col items-center justify-center rounded-md border-2 p-3 transition-colors ${
                  matchFormat === 'bestOf3'
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:border-primary/50'
                }`}
              >
                <span className="text-lg font-bold">3</span>
                <span className="text-xs text-muted-foreground">Best of 3</span>
              </button>
              <button
                type="button"
                onClick={() => onMatchFormatChange('bestOf5')}
                className={`flex flex-col items-center justify-center rounded-md border-2 p-3 transition-colors ${
                  matchFormat === 'bestOf5'
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:border-primary/50'
                }`}
              >
                <span className="text-lg font-bold">5</span>
                <span className="text-xs text-muted-foreground">Best of 5</span>
              </button>
            </div>
            <p className="text-xs text-muted-foreground">
              {matchFormat === 'bestOf1' 
                ? 'Each match consists of a single game'
                : `First to win ${matchFormat === 'bestOf3' ? '2' : '3'} games wins the match`
              }
            </p>
          </div>
          <div className="flex items-center justify-between rounded-lg border p-3">
            <div>
              <Label>Auto-Approve Scores</Label>
              <p className="text-sm text-muted-foreground">Automatically approve submitted match scores</p>
            </div>
            <div className="flex items-center gap-2">
              <span className={`text-sm ${autoApprove ? 'text-green-600' : 'text-muted-foreground'}`}>
                {autoApprove ? 'On' : 'Off'}
              </span>
              <Switch
                checked={autoApprove}
                onCheckedChange={onAutoApproveChange}
              />
            </div>
          </div>
          <div className="flex items-center justify-between rounded-lg border p-3">
            <div>
              <Label>Status</Label>
              <p className="text-sm text-muted-foreground">Archive to hide from members</p>
            </div>
            <div className="flex items-center gap-2">
              <span className={`text-sm ${status === 'active' ? 'text-green-600' : 'text-muted-foreground'}`}>
                {status === 'active' ? 'Active' : 'Archived'}
              </span>
              <Switch
                checked={status === 'active'}
                onCheckedChange={checked => onStatusChange(checked ? 'active' : 'archived')}
              />
            </div>
          </div>
          {error && (
            <p className="text-sm text-destructive">{error}</p>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button onClick={onSave} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Save Changes
            </Button>
          </div>
          
          {/* Danger Zone */}
          <div className="border-t pt-4 mt-4">
            <div className="rounded-lg border border-destructive/50 bg-destructive/5 p-4">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h4 className="text-sm font-medium text-destructive">Delete League</h4>
                  <p className="text-xs text-muted-foreground mt-1">
                    Permanently delete this league and all its data. This cannot be undone.
                  </p>
                </div>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={onDelete}
                  disabled={deleting}
                >
                  {deleting ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Trash2 className="h-4 w-4 mr-2" />
                  )}
                  Delete
                </Button>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
